import type { GroupSettingsPatch } from '#/services/settings/schema.js'
import type { SectionKey } from '#/services/settings/spec.js'
import { foldForMatch } from '#/lib/fold.js'
import { DOMAIN_RE, USERID_RE } from '#/services/settings/limits.js'
import { clearError, el, setError } from './dom.js'
import { sectionIcon } from './icons.js'
import { t } from './labels.js'
import { closeHelp, openSheet } from './sheet.js'

// The full-screen TEXT EDITOR sheet (v9, user call session 70): the editing face behind every free-text
// row (welcome/goodbye text, blocklist, allowed domains, trusted ids, rules). The old inline textareas
// were too cramped to write in; the row now opens this sheet — a tall textarea over the whole screen,
// with the field's hint as intro prose and an explicit Save button. parseList (the list-field validator
// the in-group commands mirror) moved here with the editing, PURE + unit-tested; openTextEditor is the
// thin DOM transport (live-verify only). form.ts depends on this module, never the reverse (no cycle).
//
// Commit model — the app-wide auto-save, on a bigger surface. ONE idempotent commit() (guarded by the
// last-committed value) is reached from every exit: the textarea's own `change` (blur — covers a backdrop
// or × tap, which blur the field first, AND main.ts's flushPendingEdits when the whole app closes mid-typing),
// the Save button (commits, and closes only if the text is clean), and the sheet's onClose hook (covers
// the blur-less closes: Escape and Telegram's Back). A failed validation shows the loud reason IN-sheet
// (and mirrors it to the page banner, so even a force-dismissed sheet leaves the reason visible) — Save
// refuses to close on it; a dismissal gesture still closes, dropping the bad text but never silently.

/** A list text parsed + validated against the field's rules: the clean entries, or the first problem. */
export type ListParse = { ok: true, items: string[] } | { ok: false, errKey: string, bad?: string }

// Parse a list edit (one entry per line) into the stored string[], applying the SAME rules the in-group
// commands enforce so the two paths can't diverge (the cold-eye's main finding): trim, drop blanks, lowercase
// + shape-check domains (DOMAIN_RE), per-entry length, case-insensitive dedup, and the item-count cap. Returns
// the first failing entry so the caller can name it. PURE → unit-tested without the DOM. The backend schema
// re-validates regardless (it stays the authority); this just gives precise per-line feedback before saving.
export function parseList(text: string, maxLen: number, maxItems: number, entry?: 'domain' | 'userid'): ListParse {
  const seen = new Set<string>()
  const items: string[] = []
  for (const line of text.split('\n')) {
    let v = line.trim()
    if (v.length === 0)
      continue
    if (entry === 'domain')
      v = v.toLowerCase()
    if (v.length > maxLen)
      return { ok: false, errKey: 'ui-entry-too-long', bad: v }
    if (entry === 'domain' && !DOMAIN_RE.test(v))
      return { ok: false, errKey: 'ui-bad-domain', bad: v }
    if (entry === 'userid' && !USERID_RE.test(v)) // numeric id only — NO lowercasing (digits are case-free)
      return { ok: false, errKey: 'ui-bad-userid', bad: v }
    // Dedup by the matcher's unicode fold, like /addfilter: fold-equal lines ("spam", "ｓｐａｍ") are
    // ONE rule (lib/fold.ts). For domains (lowercased + DOMAIN_RE) and numeric userids the fold is
    // the identity, so the one key fits all three list kinds.
    const key = foldForMatch(v)
    if (seen.has(key))
      continue
    seen.add(key)
    items.push(v)
  }
  if (items.length > maxItems)
    return { ok: false, errKey: 'ui-too-many' }
  return { ok: true, items }
}

/**
 * What the editor needs to know about its field — structurally satisfied by form.ts's text Control (the
 * shape is duplicated by name on purpose: importing the Control union from form.ts would cycle the deps).
 */
export interface TextEditSpec {
  label: string
  value: string
  list: boolean
  maxLen: number
  maxItems: number
  entry?: 'domain' | 'userid' | undefined
  hint?: string | undefined
  patch: (v: string | string[]) => GroupSettingsPatch
}

/**
 * Open the editor sheet for a text field. `sectionKey` picks the head glyph (the same icon as the panel
 * the row lives in); `triggerEl` (the tapped row) regains focus on close, per the shared sheet contract.
 */
export function openTextEditor(spec: TextEditSpec, sectionKey: SectionKey, onChange: (patch: GroupSettingsPatch) => void, triggerEl?: HTMLElement): void {
  const ta = el('textarea', {
    'class': 'text-input editor-input',
    'aria-label': spec.label,
    'placeholder': spec.list ? t('ui-list-hint') : t('cfg-default'),
    'maxlength': spec.list ? undefined : String(spec.maxLen),
  }, spec.value) as HTMLTextAreaElement
  // The loud in-sheet validation line (CSS hides it while empty) — the page banner alone would be COVERED
  // by the open sheet, an invisible "loud" (setError below keeps it for after a force-dismiss).
  const errLine = el('div', { class: 'editor-error' })

  // The last known-committed NORMALIZED value — commit()'s no-op guard, so the many commit routes (blur,
  // Save, onClose) can all fire for one edit and only the first writes (never an empty-diff audit row).
  let committed = spec.value
  // The bad-line message this editor put on the page banner, if any: a clean parse clears THAT one only.
  let shownBadLine: string | undefined

  // Validate + save the current text if it differs from what's committed. Returns whether the text is
  // clean (Save closes only then). A scalar can't fail client-side (maxlength caps it); a list failure
  // keeps the user's text on screen and names the first bad line (message composed in JS — web i18n
  // strings stay placeholder-free).
  function commit(): boolean {
    if (spec.list) {
      const res = parseList(ta.value, spec.maxLen, spec.maxItems, spec.entry)
      if (!res.ok) {
        const msg = res.bad !== undefined ? `${t(res.errKey)} ${res.bad.slice(0, 60)}` : t(res.errKey)
        errLine.textContent = msg
        setError(msg)
        shownBadLine = msg
        return false
      }
      errLine.textContent = ''
      // parseList succeeded → drop the stale "bad line" banner we showed, even if this turns out a no-op — only
      // while the banner still shows that message, so a save failure put there since stays (judge, s105 round 2:
      // it used to wipe any banner).
      if (shownBadLine !== undefined) {
        clearError(shownBadLine)
        shownBadLine = undefined
      }
      const v = res.items.join('\n')
      if (v !== committed) {
        committed = v
        onChange(spec.patch(res.items))
      }
      return true
    }
    const v = ta.value.trim()
    if (v !== committed) {
      committed = v
      onChange(spec.patch(v))
    }
    return true
  }

  ta.addEventListener('change', () => void commit())

  const save = el('button', { class: 'lc-btn lc-save editor-save', type: 'button' }, t('ui-save'))
  save.addEventListener('click', () => {
    if (commit())
      closeHelp()
  })

  const body: (HTMLElement | string)[] = []
  if (spec.hint !== undefined)
    body.push(el('p', { class: 'sheet-body' }, spec.hint)) // e.g. "{name} = the new member's name"
  body.push(ta, errLine, save)
  openSheet(sectionIcon(sectionKey, 'sheet-icon'), spec.label, body, { bodyKey: 'ui-editor-help' }, triggerEl, () => void commit())

  // Start writing immediately — the row was tapped to edit. Cursor at the END (focus() alone lands it at
  // the start in some engines); openSheet focused the × close button, this overrides it.
  ta.focus()
  ta.setSelectionRange(ta.value.length, ta.value.length)
}
